import { ArrowUpRight } from "lucide-react";
import { Reveal } from "../lib/motion";
import { useLang } from "../lib/i18n";
import { CtaButton, Kicker, SectionTitle, Watermark } from "./Shared";

const COL_ICONS = [
  "/images/png/icone-prb.png",
  "/images/png/icone-conséquence.png",
  "/images/png/icone-reponse.png",
];
/** 1re & 3e à −30 % par rapport à la 2e (conséquence). */
const COL_ICON_CLASS = [
  "w-[4.9rem] h-[4.9rem]",
  "w-[7rem] h-[7rem]",
  "w-[4.9rem] h-[4.9rem]",
];
const COL_ICON_CLASS_MOBILE = [
  "w-[3.75rem] h-[3.75rem]",
  "w-[5.4rem] h-[5.4rem]",
  "w-[3.75rem] h-[3.75rem]",
];
const COL_ICON_SIZE = [78, 112, 78];
const COL_ICON_SIZE_MOBILE = [60, 86, 60];
const ROW_ICONS = [
  "/images/png/icone-cadenas.png",
  "/images/png/icone-euros.png",
  "/images/png/icone-public.png",
];
const CONVICTION_IMG = "/images/conviction.webp";

/**
 * Bloc 2 — le constat.
 * Grille Problème → Conséquence → Réponse + bandeau conviction.
 */
export const Problem = () => {
  const { t } = useLang();
  const p = t.problem;

  return (
    <section
      id="constat"
      data-testid="problem-section"
      className="relative pt-32 md:pt-44 pb-28 md:pb-36 overflow-hidden"
    >
      <Watermark n="01" />

      <div className="relative z-10 max-w-site mx-auto px-6 md:px-12">
        <Reveal className="mb-14 md:mb-20 max-w-3xl">
          <Kicker>{p.kicker}</Kicker>
          <SectionTitle lead={p.title[0]} accent={p.title[1]} stacked />
          <p className="mt-7 max-w-2xl text-warm font-light leading-relaxed">
            {p.desc}
          </p>
        </Reveal>

        {/* En-têtes de colonnes — desktop */}
        <Reveal className="hidden lg:grid grid-cols-[5.5rem_1fr] gap-8 mb-8 items-center">
          <div aria-hidden />
          <div className="grid grid-cols-3 gap-8">
            {p.columns.map((label, i) => (
              <div key={label} className="flex items-center gap-3.5">
                <img
                  src={COL_ICONS[i]}
                  alt=""
                  aria-hidden
                  width={COL_ICON_SIZE[i]}
                  height={COL_ICON_SIZE[i]}
                  className={`shrink-0 object-contain ${COL_ICON_CLASS[i]}`}
                  draggable={false}
                />
                <span className="text-base uppercase tracking-[0.22em] text-cream">
                  {label}
                </span>
              </div>
            ))}
          </div>
        </Reveal>

        {/* Lignes Problème → Conséquence → Réponse */}
        <div className="flex flex-col">
          {p.rows.map((row, i) => (
            <Reveal
              key={row.title}
              delay={i * 0.06}
              className="border-t border-copper-light/35 py-10 md:py-12"
            >
              <div className="flex flex-col lg:flex-row lg:items-center gap-6 lg:gap-8">
                {/* Icône — gauche, grande, centrée verticalement */}
                <img
                  src={ROW_ICONS[i]}
                  alt=""
                  aria-hidden
                  loading="lazy"
                  decoding="async"
                  className="shrink-0 w-[4.5rem] h-[4.5rem] md:w-[5.25rem] md:h-[5.25rem] object-contain"
                  draggable={false}
                />

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 flex-1 min-w-0 lg:items-start">
                  {/* Problème */}
                  <div>
                    <p className="lg:hidden mb-4 flex items-center gap-2.5 text-sm uppercase tracking-[0.22em] text-cream">
                      <img
                        src={COL_ICONS[0]}
                        alt=""
                        aria-hidden
                        width={COL_ICON_SIZE_MOBILE[0]}
                        height={COL_ICON_SIZE_MOBILE[0]}
                        className={`shrink-0 object-contain ${COL_ICON_CLASS_MOBILE[0]}`}
                        draggable={false}
                      />
                      {p.columns[0]}
                    </p>
                    <h3 className="font-display text-lg md:text-xl font-normal text-cream leading-snug">
                      {row.title}
                    </h3>
                    <p className="mt-3 text-sm md:text-base text-dim font-light leading-relaxed">
                      {row.problem}
                    </p>
                  </div>

                  {/* Conséquence */}
                  <div>
                    <p className="lg:hidden mb-4 flex items-center gap-2.5 text-sm uppercase tracking-[0.22em] text-cream">
                      <img
                        src={COL_ICONS[1]}
                        alt=""
                        aria-hidden
                        width={COL_ICON_SIZE_MOBILE[1]}
                        height={COL_ICON_SIZE_MOBILE[1]}
                        className={`shrink-0 object-contain ${COL_ICON_CLASS_MOBILE[1]}`}
                        draggable={false}
                      />
                      {p.columns[1]}
                    </p>
                    <h3 className="font-display text-lg md:text-xl font-normal text-cream leading-snug">
                      {row.consequenceTitle}
                    </h3>
                    <p className="mt-3 text-sm md:text-base text-warm/90 font-light leading-relaxed">
                      {row.consequence}
                    </p>
                  </div>

                  {/* Réponse */}
                  <div>
                    <p className="lg:hidden mb-4 flex items-center gap-2.5 text-sm uppercase tracking-[0.22em] text-cream">
                      <img
                        src={COL_ICONS[2]}
                        alt=""
                        aria-hidden
                        width={COL_ICON_SIZE_MOBILE[2]}
                        height={COL_ICON_SIZE_MOBILE[2]}
                        className={`shrink-0 object-contain ${COL_ICON_CLASS_MOBILE[2]}`}
                        draggable={false}
                      />
                      {p.columns[2]}
                    </p>
                    <h3 className="font-display text-lg md:text-xl font-normal text-cream leading-snug">
                      {row.responseTitle}
                    </h3>
                    <p className="mt-3 text-sm md:text-base text-warm font-light leading-relaxed">
                      {row.response}
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
          <div className="border-t border-copper-light/35" aria-hidden />
        </div>

        {/* Conviction */}
        <Reveal className="mt-14 md:mt-16">
          <div className="rounded-2xl border border-copper-light/55 px-5 py-5 md:px-9 md:py-6 grid grid-cols-1 lg:grid-cols-2 gap-7 lg:gap-0 items-center">
            <div className="flex flex-col sm:flex-row items-center gap-4 md:gap-5 lg:pr-9">
              <img
                src={CONVICTION_IMG}
                alt=""
                aria-hidden
                loading="lazy"
                decoding="async"
                className="order-2 sm:order-1 shrink-0 w-[5.4rem] md:w-[7rem] h-auto object-contain self-center opacity-65 mx-auto sm:mx-0 sm:ml-2 md:ml-3"
                draggable={false}
              />
              <div className="order-1 sm:order-2 text-center sm:text-left min-w-0">
                <p className="font-display text-2xl md:text-3xl font-normal text-cream leading-snug normal-case tracking-normal">
                  {p.conviction.label}
                </p>
                <p className="mt-3 font-display text-xl md:text-2xl font-normal text-cream leading-snug">
                  {p.conviction.title[0]}
                  <br />
                  {p.conviction.title[1]}
                </p>
              </div>
            </div>

            <div className="lg:pl-10 lg:border-l lg:border-copper-light/45 text-center sm:text-left">
              <p className="text-base text-warm font-light leading-relaxed">
                {p.conviction.text}
              </p>
              <CtaButton
                variant="outline"
                href="#methode"
                data-testid="problem-approach-cta"
                className="group mt-6 border-copper-light/80 bg-copper/15 hover:border-copper-light hover:bg-copper/30"
              >
                {p.conviction.cta}
                <ArrowUpRight
                  size={14}
                  strokeWidth={1.75}
                  aria-hidden
                  className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300"
                />
              </CtaButton>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
